import { ChangeDetectionStrategy, Component, OnInit, SimpleChanges } from '@angular/core';
import { CustomizerComponentHeaderComponent, CustomizerListComponent } from '@app/customizer/components';
import { AvailabilityComponent } from '@app/shared/components/availability/availability-text.component';
import { CheckboxComponent } from '@app/shared/components/checkbox/checkbox.component';
import { ToggleComponent } from '@app/shared/components/toggle/toggle.component';
import { ComponentVariantFromApiModel } from '@models/api-models/component-variant-fromapi';
import { ComponentVariantModel } from '@models/component-variant';
import { TranslatePipe } from '@ngx-translate/core';
import { DataTranslatePipe } from '@pipes/data-translate.pipe';
import { ConfigurationService } from '@services/configuration.service';
import { EventsService } from '@services/events.service';
import { ComponentVariantChangeModel } from '../../../../_models/component-variant-change';
import { VariantSizeModel } from '../../../../_models/variant-size';
import { DataApiService } from '../../../../_services/data-api.service';
import { ImageService } from '../../../../_services/image.service';
import { SorterService } from '../../../../_services/sorter.service';
import { BaseCustomizerComponent } from './../../../../shared/base-customizer-component';

@Component({
	selector: 'temple-clip-length',
	styleUrls: ['./temple-clip-length.scss'],
	template: `
        <customizer-component-header [menu]="menu" (updateCollapsedOnComponent)="updateCollapsed($event)">
          @if (sizes?.length) {
            <div>
              @if (getSelectedVariant.SplittingAllowed && sizes && sizes.length > 1) {
                <div class="form-group has-padding-horizontal-30px flex">
                  <cstmsr-toggle [on]="getSelectedVariant.SplittingData !== null && getSelectedVariant.SplittingData !== undefined" [color]="'#33cc33'" (valueChanged)="toggle($event)">
                    {{ 'CUSTOMIZER.COMPONENTS.CLIPLENGTH.DIFFERENTLENGTHS' | translate }}
                  </cstmsr-toggle>
                </div>
              }
              @if (!getSelectedVariant.SplittingData) {
                <customizer-list
                  class="bordered"
                  [selectedIndex]="selectedIndex"
                  (selectedChange)="onSelect($event)"
                  [items]="sizes"
                  [name]="'Size'"
                  [discontinued]="'Discontinued'"
                  [editMode]="state === customizerStateEnum.EDITORDER"
                  [variantOrModelIsNew]="variantOrModelIsNew"
                  [image]="getVoid"
                ></customizer-list>
              }
              @if (getSelectedVariant.SplittingData) {
                <div class="different-lengths">
                  <div>
                    <span class="side">{{ "COMMON.LEFT" | translate }}</span>
                    <customizer-list class="bordered"
                      [selectedIndex]="selectedIndex"
                      (selectedChange)="setSize('left', $event)"
                      [items]="sizes"
                      [name]="'Size'"
                      [discontinued]="'Discontinued'"
                      [editMode]="state === customizerStateEnum.EDITORDER"
                      [variantOrModelIsNew]="variantOrModelIsNew"
                      [image]="getVoid">
                    </customizer-list>
                  </div>
                  <div>
                    <span class="side">{{ "COMMON.RIGHT" | translate }}</span>
                    <customizer-list class="bordered"
                      [selectedIndex]="selectedIndexRight"
                      (selectedChange)="setSize('right', $event)"
                      [items]="sizes"
                      [name]="'Size'"
                      [discontinued]="'Discontinued'"
                      [editMode]="state === customizerStateEnum.EDITORDER"
                      [variantOrModelIsNew]="variantOrModelIsNew"
                      [image]="getVoid">
                    </customizer-list>
                  </div>
                </div>
              }
              @if (variantList.length > 1) {
                <div class="customizer-group">
                  @for (vari of variantList; track vari) {
                    <checkbox [checked]="getSelectedVariant.Id === vari.Id" (checkedChange)="selectVariant(vari)" [label]="vari.Description| dataTranslate|translate">
                      @if (!vari.Default) {
                        <img info-content [src]="imageService.GetVariantInfoImage(vari)" />
                      }
                      <availability-text class="availability" [variantOrModelIsNew]="variantOrModelIsNew" [date]="vari.Discontinued" [new]="vari.New" [newLargeText]="false" [useNewDetails]="false"></availability-text>
                    </checkbox>
                  }
                </div>
              }
            </div>
          }
        </customizer-component-header>
        `,
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [CheckboxComponent, AvailabilityComponent, CustomizerListComponent, TranslatePipe, DataTranslatePipe, CustomizerComponentHeaderComponent, ToggleComponent]
})
export class TempleClipLengthComponent extends BaseCustomizerComponent implements OnInit {

	public sizes: Array<VariantSizeModel> | undefined = [];
	public selectedIndex: number | undefined = 0;
	public selectedIndexRight: number | undefined = 0;
	public variantList: Array<ComponentVariantFromApiModel>;

	constructor(
		public override events: EventsService,
		public apiService: DataApiService,
		public imageService: ImageService,
		private sorter: SorterService,
		public override configurationService: ConfigurationService
	) {
		super(events, apiService, configurationService);
	}

	async initData() {
		this.events.showLoader(true);

		this.variantList = await this.fetchVariants() ?? [];

		if (this.variantList.some(cv => cv.Code.includes(';'))) {
			this.overWriteVariantsList();
		}

		this.sizes = this.fetch();

		const variant = this.getSelectedVariant;
		if (variant.SplittingAllowed && this.getSelectedVariant.SplittingData) {
			this.selectedIndex = this.sizes?.findIndex(x => x.Size == this.getSelectedVariant.SplittingData?.left);
			this.selectedIndexRight = this.sizes?.findIndex(x => x.Size == this.getSelectedVariant.SplittingData?.right);
		} else {
			this.selectedIndex = this.sizes?.findIndex(x => x.Uuid == this.getSelectedVariant.VariantSize?.Uuid);
		}

		this.events.showLoader(false);
	}

	overWriteVariantsList() {
		const unique = [...new Set(this.variantList.map(item => item.Code))];

		const variants = [... new Set(unique.map(item => item.substring(0, item.lastIndexOf(';'))))];

		if (variants.length <= 1) {
			let t = this.variantList[0];
			this.variantList = [];
			this.variantList.push(t);
		}
	}

	async onChanges(changes: SimpleChanges) {
		const variant = this.getSelectedVariant;

		if (changes['variants'] && !changes['variants'].firstChange) {
			this.sizes = await this.fetch();
		}
		if (variant.SplittingAllowed && this.getSelectedVariant.SplittingData) {
			this.selectedIndex = this.sizes?.findIndex(x => x.Size == this.getSelectedVariant.SplittingData?.left);
			this.selectedIndexRight = this.sizes?.findIndex(x => x.Size == this.getSelectedVariant.SplittingData?.right);
		} else {
			this.selectedIndex = this.sizes?.findIndex(x => x.Uuid == this.getSelectedVariant.VariantSize?.Uuid);
		}

	}

	onSelect(index: number) {
		this.events.showLoader(true);

		const newVariant = Object.assign(new ComponentVariantModel(this.getSelectedVariant.Discontinued, this.getSelectedVariant.New), this.getSelectedVariant);
		const oldVariant = Object.assign(new ComponentVariantModel(this.getSelectedVariant.Discontinued, this.getSelectedVariant.New), this.getSelectedVariant);
		newVariant.VariantSize = this.sizes?.[index];
		this.updateComponentSize(new ComponentVariantChangeModel(oldVariant, newVariant, null));
	}

	selectVariant(selectedVariant: ComponentVariantFromApiModel) {
		if (selectedVariant.Id == this.getSelectedVariant.Id) {
			return; // prevents unnecessary run of script, if you select what is already selected
		}

		this.events.showLoader(true);
		this.updateVariant(selectedVariant.ConvertToChangeModel(this.getSelectedVariant, this.sorter));
	}

	fetch(): Array<VariantSizeModel> | undefined {
		const variant = this.getSelectedVariant;
		variant.VariantSizeViewed = true;

		return this.variantList.find(x => x.Id == variant.Id)?.VariantSizes.sort(this.sorter.sortBy(VariantSizeModel));
	}

	async fetchVariants(): Promise<Array<ComponentVariantFromApiModel> | undefined> {
		const apidata = await this.configurationService.getVariants(this.configuration?.ModelConfigurationUuid, this.modelGroup, this.getSelectedVariant.Component.Uuid, undefined);
		await this.RunDisablingSystem(apidata);

		return apidata;
	}

	setSize(side: string, index: number) {
		this.events.showLoader(true);
		const oldVariant = Object.assign(new ComponentVariantModel(this.getSelectedVariant.Discontinued, this.getSelectedVariant.New), this.getSelectedVariant);
		const newVariant = Object.assign(new ComponentVariantModel(this.getSelectedVariant.Discontinued, this.getSelectedVariant.New), this.getSelectedVariant);

		newVariant.VariantSize = this.sizes?.[index];

		if (side == 'left') {
			newVariant.SplittingData = { left: this.sizes?.[index].Size, right: newVariant.SplittingData?.right };
		} else {
			newVariant.SplittingData = { left: newVariant.SplittingData?.left, right: this.sizes?.[index].Size };
		}

		this.updateComponentSize(new ComponentVariantChangeModel(oldVariant, newVariant, null));
	}

	toggle(event: boolean) {
		this.events.showLoader(true);

		const newVariant = Object.assign(new ComponentVariantModel(this.getSelectedVariant.Discontinued, this.getSelectedVariant.New), this.getSelectedVariant);
		if (this.selectedIndex)
			newVariant.VariantSize = this.sizes?.[this.selectedIndex];

		if (event) {
			newVariant.SplittingData = { left: newVariant.VariantSize?.Size, right: newVariant.VariantSize?.Size };
		} else {
			newVariant.SplittingData = null;
		}

		this.updateComponentSize(new ComponentVariantChangeModel(this.getSelectedVariant, newVariant, null));
	}
	getVoid = () => {
		return '';
	}

}
